import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CalendarCheck, IndianRupee, Calculator, Scale, ArrowRight, Check,
  Building2, CalendarDays, FileText, Home, MapPin, ClipboardList, HelpCircle, Search
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CARD_ICONS = [CalendarCheck, IndianRupee, Calculator, Scale];
const INFO_ICONS = [Building2, CalendarDays, IndianRupee, Calculator, Home, MapPin, ClipboardList, HelpCircle];

const Overline = ({ children }) => (
  <p className="text-[#C6A87C] uppercase tracking-[0.22em] text-xs font-semibold font-wvc-body">{children}</p>
);
const H2 = ({ children }) => (
  <h2 className="font-wvc-display text-3xl md:text-5xl font-medium text-[#1A201C] tracking-tight mt-4">{children}</h2>
);

export const TransparencyCards = ({ section }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-24 font-wvc-body" data-testid="section-transparency-cards">
    <div className="max-w-3xl">
      <Overline>Why WVC</Overline>
      <H2>{section.heading}</H2>
      <p className="text-[#3E4C43] mt-5 leading-relaxed">{section.copy}</p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
      {(section.items || []).map((item, i) => {
        const Icon = CARD_ICONS[i % CARD_ICONS.length];
        return (
          <div key={i} className="bg-white rounded-2xl border border-[#1A3626]/10 p-7 hover:shadow-[0_8px_24px_rgba(26,54,38,0.06)] transition-shadow" data-testid={`transparency-card-${i}`}>
            <Icon className="w-6 h-6 text-[#1A3626]" strokeWidth={1.5} />
            <h3 className="font-wvc-display text-xl font-semibold text-[#1A201C] mt-4">{item.title}</h3>
            <p className="text-sm text-[#3E4C43] mt-2 leading-relaxed">{item.description}</p>
          </div>
        );
      })}
    </div>
  </section>
);

export const HowItWorks = ({ section }) => (
  <section id="how-it-works" className="bg-[#F3F1E7] py-20 md:py-24 font-wvc-body" data-testid="section-how-it-works">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="text-center max-w-2xl mx-auto">
        <Overline>How WVC Works</Overline>
        <H2>{section.heading}</H2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-14">
        {(section.items || []).map((step, i) => (
          <div key={i} className="relative" data-testid={`how-step-${i}`}>
            <div className="w-12 h-12 rounded-full bg-[#1A3626] text-white font-wvc-display text-xl flex items-center justify-center">{i + 1}</div>
            <h3 className="font-wvc-display text-xl font-semibold text-[#1A201C] mt-5">{step.title}</h3>
            <p className="text-sm text-[#3E4C43] mt-2 leading-relaxed">{step.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const CalculatorShowcase = ({ section }) => {
  const navigate = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-24 font-wvc-body" data-testid="section-calculator">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <Overline>The Venue Calculator</Overline>
          <H2>{section.heading}</H2>
          <p className="text-[#3E4C43] mt-5 leading-relaxed">{section.copy}</p>
          <button
            onClick={() => navigate(section.cta_url || '/venues')}
            className="mt-8 bg-[#1A3626] text-white hover:bg-[#254834] rounded-full px-8 py-4 text-sm inline-flex items-center gap-2 transition-all duration-300"
            data-testid="calculator-cta"
          >
            {section.cta_label} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="bg-[#1A3626] rounded-2xl p-8 text-white">
          <p className="text-[10px] uppercase tracking-[0.22em] text-white/50">Sample venue estimate</p>
          <div className="mt-5 space-y-3">
            {(section.items || []).map((row, i) => (
              <div key={i} className={`flex justify-between text-sm ${i === (section.items.length - 1) ? 'border-t border-white/15 pt-4 mt-4' : ''}`}>
                <span className="text-white/70">{row.title}</span>
                <span className={i === (section.items.length - 1) ? 'font-wvc-display text-lg' : ''}>{row.description}</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-white/40 mt-6 leading-relaxed">Every calculator is venue-specific and uses the pricing structure configured by that venue. Estimates are indicative and subject to final confirmation.</p>
        </div>
      </div>
    </section>
  );
};

export const TransparencyPromise = ({ section }) => (
  <section className="bg-[#1A201C] py-20 md:py-24 font-wvc-body relative overflow-hidden" data-testid="section-transparency-promise">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
      <Overline>Our Promise</Overline>
      <h2 className="font-wvc-display text-3xl md:text-5xl font-medium text-white tracking-tight mt-4">{section.heading}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10 mt-14">
        {(section.items || []).map((item, i) => (
          <div key={i} className="border-t border-white/15 pt-5" data-testid={`promise-${i}`}>
            <h3 className="font-wvc-display text-xl text-white">{item.title}</h3>
            <p className="text-sm text-white/60 mt-2 leading-relaxed">{item.description}</p>
          </div>
        ))}
      </div>
      {section.tagline && (
        <p className="font-wvc-display text-2xl md:text-3xl text-[#C6A87C] mt-14 text-center">{section.tagline}</p>
      )}
    </div>
  </section>
);

export const CategoriesBento = ({ section }) => {
  const navigate = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-24 font-wvc-body" data-testid="section-categories">
      <Overline>Explore</Overline>
      <H2>{section.heading}</H2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
        {(section.items || []).map((cat, i) => (
          <button
            key={i}
            onClick={() => navigate(cat.url || '/venues')}
            className={`relative rounded-2xl overflow-hidden group text-left ${i === 0 ? 'col-span-2 row-span-2 aspect-square lg:aspect-auto' : 'aspect-[4/3]'}`}
            data-testid={`category-tile-${i}`}
          >
            <img src={cat.image} alt={cat.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A201C]/80 via-transparent to-transparent" />
            <span className="absolute bottom-4 left-4 right-4 font-wvc-display text-lg md:text-xl text-white">{cat.title}</span>
          </button>
        ))}
      </div>
    </section>
  );
};

export const CompareTeaser = ({ section }) => {
  const navigate = useNavigate();
  if (section.mode === 'hidden') return null;
  return (
    <section className="bg-[#F3F1E7] py-20 md:py-24 font-wvc-body" data-testid="section-compare">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <Overline>Compare Venues {section.mode === 'coming_soon' && '· Coming Soon'}</Overline>
        <H2>{section.heading}</H2>
        <p className="text-[#3E4C43] mt-5 leading-relaxed max-w-2xl mx-auto">{section.copy}</p>
        {section.mode === 'active' && (
          <button onClick={() => navigate(section.cta_url || '/compare')}
            className="mt-8 bg-[#1A3626] text-white hover:bg-[#254834] rounded-full px-8 py-4 text-sm transition-all duration-300" data-testid="compare-cta">
            {section.cta_label}
          </button>
        )}
      </div>
    </section>
  );
};

export const RealInfo = ({ section }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-24 font-wvc-body" data-testid="section-real-info">
    <div className="max-w-3xl">
      <Overline>Real Venue Information</Overline>
      <H2>{section.heading}</H2>
      <p className="text-[#3E4C43] mt-5 leading-relaxed">{section.copy}</p>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
      {(section.items || []).map((item, i) => {
        const Icon = INFO_ICONS[i % INFO_ICONS.length];
        return (
          <div key={i} className="flex items-center gap-3 bg-white border border-[#1A3626]/10 rounded-xl px-4 py-4" data-testid={`info-block-${i}`}>
            <Icon className="w-5 h-5 text-[#C6A87C] flex-shrink-0" strokeWidth={1.5} />
            <span className="text-sm text-[#1A201C]">{item.title}</span>
          </div>
        );
      })}
    </div>
  </section>
);

export const OwnersSection = ({ section, onPartner }) => (
  <section id="for-venue-owners" className="bg-[#1A3626] py-20 md:py-24 font-wvc-body" data-testid="section-owners">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div>
        <p className="text-[#E5D8C6] uppercase tracking-[0.22em] text-xs font-semibold">For Venue Owners</p>
        <h2 className="font-wvc-display text-3xl md:text-5xl font-medium text-white tracking-tight mt-4">{section.heading}</h2>
        <p className="text-white/70 mt-5 leading-relaxed">{section.copy}</p>
        <div className="flex flex-wrap gap-3 mt-8">
          <button onClick={onPartner}
            className="bg-white text-[#1A3626] hover:bg-[#E5D8C6] rounded-full px-8 py-4 text-sm transition-all duration-300" data-testid="partner-cta">
            {section.cta_label || 'Partner With WVC'}
          </button>
        </div>
        {section.note && <p className="text-white/50 text-xs mt-6">{section.note}</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(section.items || []).map((b, i) => (
          <div key={i} className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-xl px-4 py-3.5" data-testid={`owner-benefit-${i}`}>
            <Check className="w-4 h-4 text-[#C6A87C] flex-shrink-0" strokeWidth={1.5} />
            <span className="text-sm text-white/85">{b.title}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const AvailabilityEducation = ({ section }) => (
  <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center font-wvc-body" data-testid="section-availability-education">
    <h2 className="font-wvc-display text-2xl md:text-3xl font-medium text-[#1A201C]">{section.heading}</h2>
    <p className="text-[#3E4C43] mt-4 leading-relaxed">{section.copy}</p>
  </section>
);

export const StatsAndTestimonials = ({ section }) => {
  const activeStats = (section.stats || []).filter(s => s.active);
  const testimonials = (section.testimonials || []).filter(t => t.visible !== false);
  if (!activeStats.length && !testimonials.length) return null;
  return (
    <section className="bg-[#F3F1E7] py-16 font-wvc-body" data-testid="section-stats">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {activeStats.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {activeStats.map((s, i) => (
              <div key={i} className="text-center" data-testid={`stat-${i}`}>
                <p className="font-wvc-display text-3xl md:text-4xl text-[#1A3626]">{s.value}</p>
                <p className="text-xs uppercase tracking-[0.15em] text-[#748278] mt-2">{s.label}</p>
              </div>
            ))}
          </div>
        )}
        {testimonials.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#1A3626]/10 p-6" data-testid={`testimonial-${i}`}>
                <p className="text-sm text-[#3E4C43] leading-relaxed">"{t.text}"</p>
                <p className="text-sm font-semibold text-[#1A201C] mt-4">{t.name}</p>
                <p className="text-xs text-[#748278]">{[t.customer_type, t.venue_name].filter(Boolean).join(' · ')}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export const HomeFAQs = ({ section }) => (
  <section className="max-w-4xl mx-auto px-4 sm:px-6 py-20 md:py-24 font-wvc-body" data-testid="section-faqs">
    <div className="text-center">
      <Overline>Questions</Overline>
      <H2>{section.heading}</H2>
    </div>
    <Accordion type="single" collapsible className="mt-10 border-t border-[#1A3626]/10">
      {(section.items || []).map((f, i) => (
        <AccordionItem key={i} value={`hfaq-${i}`} className="border-b border-[#1A3626]/10">
          <AccordionTrigger className="text-left font-wvc-display text-lg text-[#1A201C] py-5 hover:no-underline" data-testid={`home-faq-${i}`}>{f.q}</AccordionTrigger>
          <AccordionContent className="text-[#3E4C43] leading-relaxed pb-5">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

export const FinalCTA = ({ section }) => {
  const navigate = useNavigate();
  return (
    <section className="bg-[#1A201C] py-20 md:py-24 font-wvc-body" data-testid="section-final-cta">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
        <h2 className="font-wvc-display text-3xl md:text-5xl font-medium text-white tracking-tight">{section.heading}</h2>
        <p className="text-white/70 mt-5 leading-relaxed">{section.copy}</p>
        <div className="flex flex-col sm:flex-row justify-center gap-3 mt-9">
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="bg-white text-[#1A201C] hover:bg-[#E5D8C6] rounded-full px-8 py-4 text-sm inline-flex items-center justify-center gap-2 transition-all duration-300" data-testid="final-search-btn">
            <Search className="w-4 h-4" strokeWidth={1.5} /> Search by Dates
          </button>
          <button onClick={() => navigate('/venues')}
            className="border border-white/40 text-white hover:bg-white/10 rounded-full px-8 py-4 text-sm transition-all duration-300" data-testid="final-browse-btn">
            Browse All Venues
          </button>
        </div>
      </div>
    </section>
  );
};

export const HomeFooter = () => (
  <footer className="bg-[#12160F] text-white/70 py-14 font-wvc-body" data-testid="home-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 md:grid-cols-4 gap-10">
      <div className="col-span-2 md:col-span-1">
        <p className="font-wvc-display text-4xl text-white font-bold">WVC<span className="text-[#C6A87C]">.</span></p>
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#E5D8C6] mt-2">A Happy Retreats Company</p>
      </div>
      <div className="space-y-2 text-sm">
        <p className="text-white text-xs uppercase tracking-[0.18em] mb-3">Discover</p>
        <Link to="/" className="block hover:text-white">Find Venues</Link>
        <Link to="/venues" className="block hover:text-white">Browse All Venues</Link>
        <Link to="/intelligence" className="block hover:text-white">Venue Intelligence</Link>
        <Link to="/community" className="block hover:text-white">Community</Link>
      </div>
      <div className="space-y-2 text-sm">
        <p className="text-white text-xs uppercase tracking-[0.18em] mb-3">Venues</p>
        <a href="#for-venue-owners" className="block hover:text-white">For Venue Owners</a>
        <Link to="/admin" className="block hover:text-white">Venue Owner Login</Link>
      </div>
      <div className="space-y-2 text-sm">
        <p className="text-white text-xs uppercase tracking-[0.18em] mb-3">Company</p>
        <span className="block">About WVC</span>
        <span className="block">Privacy Policy</span>
        <span className="block">Terms</span>
        <span className="block">Disclaimer</span>
      </div>
    </div>
    <p className="text-center text-xs text-white/40 mt-12">© {new Date().getFullYear()} Wedding Venues Company · Venue-provided information, pricing subject to final confirmation</p>
  </footer>
);

export const PartnerDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({ venue_name: '', contact_name: '', phone: '', email: '', city: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!form.venue_name || !form.contact_name || !form.phone) {
      toast.error('Please fill venue name, your name and phone');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/partner/enquiry`, form);
      toast.success('Thank you! The WVC partnerships team will reach out shortly.');
      onOpenChange(false);
      setForm({ venue_name: '', contact_name: '', phone: '', email: '', city: '', message: '' });
    } catch {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md font-wvc-body">
        <DialogHeader>
          <DialogTitle className="font-wvc-display text-2xl">Partner With WVC</DialogTitle>
          <DialogDescription>Tell us about your venue and our team will get in touch.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Input placeholder="Venue name *" value={form.venue_name} onChange={e => setForm(p => ({ ...p, venue_name: e.target.value }))} data-testid="partner-venue-input" />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Your name *" value={form.contact_name} onChange={e => setForm(p => ({ ...p, contact_name: e.target.value }))} data-testid="partner-name-input" />
            <Input placeholder="Phone *" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} data-testid="partner-phone-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} data-testid="partner-email-input" />
            <Input placeholder="City" value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} data-testid="partner-city-input" />
          </div>
          <Textarea placeholder="About your venue (optional)" value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} data-testid="partner-message-input" />
          <Button onClick={submit} disabled={submitting} className="w-full bg-[#1A3626] hover:bg-[#254834] rounded-full" data-testid="partner-submit-btn">
            {submitting ? 'Submitting...' : 'Submit Partnership Enquiry'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
